
import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { DmttReturnTokenValidatorService } from '../services/dmtt-return-token-validator.service';
import { SessionStorageService } from '../services/session-storage.service';
import { ReturnHydrationService } from '../services/return-hydration.service';
import { FormDataService } from '../services/form-data.service';
import { environment } from '../../../environments/environment';
import { BinService } from '../services/bin.service';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  constructor(
    private readonly tokenValidator: DmttReturnTokenValidatorService,
    private readonly sessionStorageService: SessionStorageService,
    private readonly router: Router,
    private readonly hydrationService: ReturnHydrationService,
    private readonly formDataService: FormDataService,
    private readonly binService: BinService
  ) {}

  private redirectToOtb(): boolean {
    const target = String((environment as any).otbSignupUrl || 'https://dev.login.onetaxbahamas.gov.bs/signup/dmtt');
    const separator = target.includes('?') ? '&' : '?';
    window.location.replace(`${target}${separator}dmtt_logout=1`);
    return false;
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    try {
      const encryptedToken = route.queryParamMap.get('dmtt_token');

      if (encryptedToken) {
        if (this.tokenValidator.validateAndStore(encryptedToken)) {
          this.persistBinNumber();
          this.persistReturnCaseId(route);
          // Strip sensitive/one-time params — use full state.url path to
          // preserve deep links like /returns/introductory-questions
          const cleanPath = state.url.split('?')[0];
          const cleanParams: Record<string, string> = {};
          route.queryParamMap.keys.forEach(key => {
            if (key !== 'dmtt_token' && key !== 'return_case_id') {
              cleanParams[key] = route.queryParamMap.get(key)!;
            }
          });
          return this.router.createUrlTree([cleanPath], { queryParams: cleanParams });
        }
        this.tokenValidator.clearSession();
        return this.redirectToOtb();
      }

      if (this.tokenValidator.hasValidSession()) {
        this.persistBinNumber();
        return true;
      }

      this.tokenValidator.clearSession();
      return this.redirectToOtb();
    } catch {
      this.tokenValidator.clearSession();
      return this.redirectToOtb();
    }
  }

  private persistBinNumber(): void {
    const bin = this.tokenValidator.getSessionPayload()?.profile?.binNumber;
    if (bin) {
      this.binService.setBin(bin);
    }
  }

  private persistReturnCaseId(route: ActivatedRouteSnapshot): void {
    const returnCaseId = route.queryParamMap.get('return_case_id');
    const applicationRole = this.resolveApplicationRole(route);
    if (returnCaseId) {
      // Clear stale form data and hydration cache so the GET API always
      // re-fires when navigating to a different (or same) return case.
      this.formDataService.clearAll();
      this.hydrationService.resetCache();
      this.sessionStorageService.setApplicationId(returnCaseId);
    }

    if (applicationRole) {
      this.sessionStorageService.setApplicationRole(applicationRole);
    }
  }

  private resolveApplicationRole(route: ActivatedRouteSnapshot): string {
    const fromQuery = String(
      route.queryParamMap.get('application_role')
      || route.queryParamMap.get('role')
      || ''
    ).trim();

    if (fromQuery) {
      return fromQuery;
    }

    const profile = this.tokenValidator.getSessionPayload()?.profile;
    const candidates = [
      profile?.taxType,
      ...(Array.isArray(profile?.persona) ? profile.persona : []),
      ...(Array.isArray(profile?.taxRepPersona) ? profile.taxRepPersona : [])
    ];

    return String(candidates.find((value) => String(value || '').trim()) || '').trim();
  }
}
